/**
 * RSVP Reminder Test Page (Hidden Admin Page)
 *
 * Allows admins to trigger a test RSVP reminder email.
 * Not linked from any navigation menu or other page.
 *
 * Requirements: 9.1, 9.3, 9.6
 */

import { useState, useCallback } from 'react';
import { restClient } from '../../api/rest-client';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

interface ReminderTestResult {
  message: string;
  events_found: number;
  riders_identified: number;
  email_sent: boolean;
}

export default function RsvpReminderTest() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ReminderTestResult | null>(null);

  const handleSendTest = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = await restClient.sendRsvpReminderTest();
      setResult(data);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to send test reminder email';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">RSVP Reminder Test</h1>
        <p className="text-gray-600">
          Send a test RSVP reminder email to yourself to verify content and formatting.
        </p>
      </div>

      {/* Send Button */}
      <Card className="mb-6">
        <div className="p-6">
          <Button onClick={handleSendTest} disabled={loading} className="w-full">
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <LoadingSpinner size="sm" />
                Sending...
              </span>
            ) : (
              'Send Test Email'
            )}
          </Button>
        </div>
      </Card>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Results Display */}
      {result && (
        <Card>
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Results</h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-gray-600">Events Found</dt>
                <dd className="font-semibold text-gray-900">{result.events_found}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Riders Identified</dt>
                <dd className="font-semibold text-gray-900">{result.riders_identified}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Email Sent</dt>
                <dd className="font-semibold">
                  {result.email_sent ? (
                    <span className="text-green-600">✓ Yes</span>
                  ) : (
                    <span className="text-yellow-600">✗ No</span>
                  )}
                </dd>
              </div>
              {result.message && (
                <div className="pt-3 border-t border-gray-200">
                  <dt className="text-gray-600 mb-1">Message</dt>
                  <dd className="text-gray-900">{result.message}</dd>
                </div>
              )}
            </dl>
          </div>
        </Card>
      )}
    </div>
  );
}
